"use client";

import { MapPinIcon } from "@heroicons/react/24/outline";
import React, { useState, useRef, useEffect, FC } from "react";
import ClearDataButton from "./ClearDataButton";
import { useClient } from "ClientContext";
import { useGetLocations } from "hooks/useLocations";
import { useGetListings } from "hooks/useGetListings";
import { useTranslation } from "react-i18next";

export interface LocationInputProps {
  placeHolder?: string;
  desc?: string;
  className?: string;
  divHideVerticalLineClass?: string;
  autoFocus?: boolean;
  onChange?: (value: any) => void;
}

const LocationInput: FC<LocationInputProps> = ({
  autoFocus = false,
  placeHolder = "",
  desc = "",
  className = "nc-flex-1.5",
  divHideVerticalLineClass = "left-10 -right-0.5",
  onChange,
}) => {
//handle translation
  const { t } = useTranslation();
  placeHolder = t("Location");
  desc = t("Where are you going?");

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {handleSearch} = useGetListings();
  const { setGlobal_search, global_searh } = useClient();
  const location = global_searh?.params?.location;
  const initialCity = location?.city ?? "";
  const initialState = location?.state ?? "";

  // Selected location
  const [value, setValue] = useState<{ city: string; state: string }>({
    city: initialCity,
    state: initialState,
  });
  // User input filter
  const [filter, setFilter] = useState<string>("");
  const [showPopover, setShowPopover] = useState(autoFocus);

  const { locations } = useGetLocations();

  const hideCountry = React.useMemo(() => {
    if (!locations || locations.length === 0) return true;
    const firstCountry = locations[0].country;
    return locations.every((loc) => loc.country === firstCountry);
  }, [locations]);

  // Sync autoFocus
  useEffect(() => setShowPopover(autoFocus), [autoFocus]);

  // Outside click to close
  useEffect(() => {
    if (showPopover) document.addEventListener("click", onClickOutside);
    return () => document.removeEventListener("click", onClickOutside);
  }, [showPopover]);

  const onClickOutside = (e: MouseEvent) => {
    if (!containerRef.current) return;
    if (!showPopover || containerRef.current.contains(e.target as Node)) return;
    setShowPopover(false);
  };

  // Auto-focus input on open
  useEffect(() => { if (showPopover && inputRef.current) inputRef.current.focus(); }, [showPopover]);

  // Select a location
  const handleSelectLocation = (item: { city: string; state?: string; country?: string }) => {
    const city = item.city;
    const state = item.state || "";
    setValue({ city, state });
    setFilter("");
    setGlobal_search(prev => ({
      ...prev,
      params: {
        ...prev.params,
        location: { city, state, country: item.country || "" },
      },
    }));
    setShowPopover(false);
    onChange?.(item);
  };

  // Filter list
  const filtered = locations?.filter((loc: any) =>
    loc.city.toLowerCase().includes(filter.toLowerCase())
  );

  const renderList = () => (
    <>
      <h3 className="block mt-2 sm:mt-0 px-4 sm:px-8 font-semibold text-base sm:text-lg text-neutral-800 dark:text-neutral-100">
        Available Cities
      </h3>
      <div className="mt-2 max-h-64 overflow-auto">
        {filtered.length > 0 ? (
          filtered.map((item: any, idx: number) => (
            <div
              key={idx}
              onClick={() => handleSelectLocation(item)}
              className="flex px-4 sm:px-8 items-center space-x-3 sm:space-x-4 py-4 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
            >
              <MapPinIcon className="h-4 sm:h-6 w-4 sm:w-6 text-neutral-400" />
              <span className="block font-medium text-neutral-700 dark:text-neutral-200">
                {item.city ? `${item.city}, ` : ""}{item.state}{!hideCountry && `, ${item.country}`}
              </span>
            </div>
          ))
        ) : (
          <div className="px-4 sm:px-8 py-4 text-neutral-500">No matching cities</div>
        )}
      </div>
    </>
  );

  return (
    <div className={`relative flex ${className}`} ref={containerRef}>
      <div
        onClick={() => setShowPopover(true)}
        className={`flex z-10 flex-1 [ nc-hero-field-padding ] flex-shrink-0 items-center space-x-3 cursor-pointer text-left ${
          showPopover ? "nc-hero-field-focused" : ""
        }`}
      >
        <MapPinIcon className="w-5 h-5 lg:w-7 lg:h-7 text-neutral-300 dark:text-neutral-400" />
        <div className="flex-grow relative">
          <input
            ref={inputRef}
            className="block w-full bg-transparent border-none focus:ring-0 p-0 focus:outline-none placeholder-neutral-800 dark:placeholder-neutral-200 xl:text-lg font-semibold truncate"
            placeholder={(!filter && !value.city) ? placeHolder : ""}
            value={filter !== "" ? filter : value.city || value.state}
            onChange={(e) => {
              setFilter(e.target.value);
              // clear selected when typing
              setValue({ city: "", state: "" });
              setGlobal_search(prev => ({
                ...prev,
                params: {
                  ...prev.params,
                  location: { city:e.target.value, state:"", country: ""  },
                },
              }));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
                setShowPopover(false);
              }
            }}
          />
          <span className="block mt-0.5 text-sm text-neutral-400 font-light">
            {!filter && !value.city ? desc : ""}
          </span>
          {(filter || value.city || value.state) && (
            <ClearDataButton
              onClick={() => {
                setFilter("");
                setValue({ city: "", state: "" });
                setGlobal_search(prev => ({
                  ...prev,
                  isSearching: false,
                  params: {
                    ...prev.params,
                    location: { city: "", state: "", country: "" },
                  },
                }));
              }}
            />
          )}
        </div>
      </div>

      {showPopover && (
        <div
          className={`h-8 absolute self-center top-1/2 -translate-y-1/2 z-0 bg-white dark:bg-neutral-800 ${divHideVerticalLineClass}`}
        />
      )}

      {showPopover && (
        <div className="absolute left-0 z-40 w-full min-w-[300px] sm:min-w-[500px] bg-white dark:bg-neutral-800 top-full mt-3 py-3 sm:py-6 rounded-3xl shadow-xl overflow-y-auto">
          {renderList()}
        </div>
      )}
    </div>
  );
};

export default LocationInput;
